import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgModule, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingAttributeComponent, ListLoadingIndicatorComponent } from '@shared/ui';
import { PipelineEnvironment } from '@telespot/domain';
import { TranslateModule } from '@ngx-translate/core';

export interface PipelineListItem {
  id: string;
  name: { [key: string]: string };
  env: PipelineEnvironment;
  /**
   * Creation date of the pipeline (carried so list-driven commands keep it)
   */
  createdAt: Date;
  usedByMethodTypes?: number;
  numSteps?: number;
  /** 
   * Whether this pipeline is assigned to the current container (drives the ASSIGNED column)
   */
  assigned?: boolean;
  /** 
   * True for staged rows that exist only client-side (not yet persisted)
   */
  pending?: boolean;
}

export enum PipelineColumn {
  NAME = 'name',
  ENVIRONMENT = 'environment',
  METHOD_TYPES = 'usedByMethodTypes',
  STEPS = 'numSteps',
  EDIT = 'edit',
  ASSIGNED = 'assigned',
}

@Component({
    selector: 'ts-pipelines-list',
    templateUrl: './pipelines-list.component.html',
    styleUrls: ['./pipelines-list.component.scss'],
    standalone: false
})
export class PipelinesListComponent implements OnChanges {
  @Input() items: PipelineListItem[] = [];
  @Input() loading = false;
  @Input() noPipelines = false;
  @Input() currentLanguage = 'en';
  @Input() emptyMessageKey = 'info.no_pipelines';
  @Input() columns: PipelineColumn[] = [
    PipelineColumn.NAME,
    PipelineColumn.ENVIRONMENT,
    PipelineColumn.METHOD_TYPES,
    PipelineColumn.STEPS,
    PipelineColumn.EDIT,
  ];

  @Output() editClicked = new EventEmitter<PipelineListItem>();
  @Output() assignedToggled = new EventEmitter<PipelineListItem>();

  public readonly PipelineColumn = PipelineColumn;

  get visibleColumns(): string[] {
    return this.columns.map((col) => col.valueOf());
  }

  // Store the container height when loading starts to prevent layout jumps
  public containerHeight: number | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When loading starts, capture the current height
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    // When loading ends, clear the fixed height
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }

  public getPipelineName(name: { [key: string]: string }): string {
    if (!name) {
      return '';
    }
    const currentName = name[this.currentLanguage];
    if (currentName && currentName.trim() !== '') {
      return currentName;
    }
    return name['en'] ?? Object.values(name)[0] ?? '';
  }

  public onEditClick(item: PipelineListItem): void {
    this.editClicked.emit(item);
  }
}

@NgModule({
  declarations: [PipelinesListComponent],
  exports: [PipelinesListComponent],
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LoadingAttributeComponent,
    ListLoadingIndicatorComponent,
  ],
})
export class PipelinesListComponentModule {}
